/* Het websitescherm (ADR 0034): vanuit één scherm zien en bedienen wat er op greenvision.energy
 * gebeurt. Bovenaan vier cijfers, daaronder de formulieren, het bezoek en de inhoud naast elkaar,
 * onderaan wat er laatst gebeurde. Alles komt uit één samenvatting (migratie 110); de knoppen gaan
 * naar de bestaande schermen van Websitecontent. Hier wordt niets geschreven. */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, ExternalLink, Globe, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useOverzicht } from './hooks';
import {
  aantalSignalen, geledenKort, herkomstAandeel, INZENDING_LABEL, leadWacht, OverzichtFout, signaalTeksten, stadiumLabel, toolLabel, VENSTERS,
  verschilProcent, verversTekst, type Bezoek, type Formulieren, type Gebeurtenis, type InhoudStand, type Overzicht as OverzichtData, type Signalen, type Venster,
} from './data/overzicht';
import { SOORTEN } from './data/website';
import { DOORVERWIJZINGEN_PAD, INSTELLINGEN_PAD, MEDIA_PAD, PAGINAS_PAD } from './WebsiteBeheer';

const SITE = 'https://greenvision.energy';
const SITE_PAD = '/crm/website/site';
const GETAL = new Intl.NumberFormat('nl-NL');

/* ── bouwstenen ─────────────────────────────────────────────────────── */

function Tegel({ label, waarde, onder, dringend, naar }: { label: string; waarde: string; onder?: string | null; dringend?: boolean; naar?: string }) {
  const inhoud = (
    <>
      <div className={cn('text-xs', dringend ? 'text-red-700' : 'text-muted-foreground')}>{label}</div>
      <div className={cn('text-2xl font-semibold leading-tight', dringend && 'text-red-700')}>{waarde}</div>
      {onder && <div className={cn('mt-0.5 truncate text-xs', dringend ? 'text-red-700' : 'text-muted-foreground')}>{onder}</div>}
    </>
  );
  const klassen = cn('block rounded-lg px-4 py-3', dringend ? 'bg-red-50 dark:bg-red-950/30' : 'bg-muted/50', naar && 'hover:bg-muted');
  return naar ? <Link to={naar} className={klassen}>{inhoud}</Link> : <div className={klassen}>{inhoud}</div>;
}

function Vak({ titel, rechts, children, className }: { titel: string; rechts?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn('flex min-w-0 flex-col overflow-hidden rounded-xl border bg-background', className)} aria-label={titel}>
      <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
        <h2 className="text-sm font-semibold">{titel}</h2>
        {rechts}
      </div>
      {children}
    </section>
  );
}

const verschilTekst = (nu: number, vorig: number, dagen: number): string | null => {
  const p = verschilProcent(nu, vorig);
  if (p === null) return null;
  return `${p > 0 ? '+' : ''}${p}% t.o.v. de ${dagen} dagen ervoor`;
};

/* ── formulieren ────────────────────────────────────────────────────── */

function FormulierenVak({ f }: { f: Formulieren }) {
  const leads = f.leads.slice(0, 8);
  return (
    <Vak titel="Formulieren en aanvragen" rechts={<Link to="/crm/leads" className="text-xs text-muted-foreground hover:text-foreground">Alle leads</Link>}>
      {leads.length === 0 && f.inzendingen.length === 0 ? (
        <p className="px-4 py-3 text-sm text-muted-foreground">Nog geen formulieren via de site in de laatste 30 dagen.</p>
      ) : (
        <ul className="divide-y">
          {leads.map((l) => {
            const wacht = leadWacht(l);
            const wie = [l.naam, l.bedrijf].filter(Boolean).join(' · ') || l.email || l.summary || 'Onbekend';
            return (
              <li key={l.id} className={cn('border-l-[3px] px-3 py-2.5 text-sm', wacht && !l.is_test ? 'border-l-red-500' : 'border-l-transparent')}>
                <Link to={`/crm/leads/${l.id}`} className="block hover:underline">
                  <span className="font-medium">{wie}</span>
                  {l.is_test && <span className="ml-2 rounded bg-muted px-1.5 py-px text-xs text-muted-foreground">proef</span>}
                </Link>
                <div className="mt-0.5 truncate text-xs text-muted-foreground">
                  {[l.onderwerp ?? l.label, l.pagina ? `via ${l.pagina}` : null, geledenKort(l.created_at)].filter(Boolean).join(' · ')}
                </div>
                <div className={cn('text-xs', wacht && !l.is_test ? 'font-medium text-red-700' : 'text-muted-foreground')}>
                  {stadiumLabel(l.stage)}{!wacht && l.eigenaar ? ` door ${l.eigenaar}` : ''}
                </div>
              </li>
            );
          })}
          {f.inzendingen.slice(0, 3).map((i, n) => (
            <li key={`${i.created_at}-${n}`} className="border-l-[3px] border-l-transparent px-3 py-2.5 text-sm">
              <span className="font-medium">{INZENDING_LABEL[i.kind] ?? i.kind}</span>
              {i.vacancy_slug && <span className="text-muted-foreground">: {i.vacancy_slug}</span>}
              <div className="text-xs text-muted-foreground">{geledenKort(i.created_at)}{i.is_test ? ' · proef' : ''}</div>
            </li>
          ))}
        </ul>
      )}
    </Vak>
  );
}

/* ── bezoek ─────────────────────────────────────────────────────────── */

function Lijn({ dagen }: { dagen: Bezoek['per_dag'] }) {
  const max = Math.max(1, ...dagen.map((d) => d.weergaven));
  const b = 300, h = 48;
  const stap = dagen.length > 1 ? b / (dagen.length - 1) : b;
  const punten = dagen.map((d, i) => `${(i * stap).toFixed(1)},${(h - 4 - (d.weergaven / max) * (h - 8)).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${b} ${h}`} className="h-12 w-full" role="img" aria-label={`Weergaven per dag, hoogste ${max}`}>
      <polyline fill="none" stroke="currentColor" strokeWidth="2" className="text-emerald-600" points={punten} />
    </svg>
  );
}

function BezoekVak({ b, venster, zetVenster }: { b: Bezoek; venster: Venster; zetVenster: (v: Venster) => void }) {
  const herkomst = herkomstAandeel(b.herkomst);
  return (
    <Vak
      titel="Bezoek"
      rechts={
        <div className="flex gap-1 text-xs" role="group" aria-label="Venster">
          {VENSTERS.map((v) => (
            <button key={v} type="button" onClick={() => zetVenster(v)} aria-pressed={v === venster}
              className={cn('rounded px-1.5 py-0.5', v === venster ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:text-foreground')}>
              {v} d
            </button>
          ))}
        </div>
      }
    >
      <div className="px-4 pt-3">
        <div className="flex items-baseline gap-3">
          <span className="text-2xl font-semibold">{GETAL.format(b.sessies)}</span>
          <span className="text-xs text-muted-foreground">bezoekers · {GETAL.format(b.weergaven)} weergaven</span>
        </div>
        {b.sessies > 0 || b.sessies_vorig > 0 ? <Lijn dagen={b.per_dag} /> : <p className="py-3 text-sm text-muted-foreground">Nog geen bezoek in dit venster.</p>}
      </div>
      <ul className="px-4 pb-2 text-xs">
        {b.paden.slice(0, 6).map((p) => (
          <li key={p.pad} className="flex justify-between gap-2 border-t py-1">
            <a href={`${SITE}${p.pad}`} target="_blank" rel="noreferrer" className="truncate hover:underline">{p.pad}</a>
            <span className="shrink-0 text-muted-foreground">{GETAL.format(p.weergaven)}</span>
          </li>
        ))}
        {herkomst.length > 0 && (
          <li className="flex flex-wrap justify-between gap-x-2 border-t pt-1.5 text-muted-foreground">
            <span>Herkomst</span>
            <span>{herkomst.map((h) => `${h.label} ${h.procent}%`).join(' · ')}</span>
          </li>
        )}
        {b.tools.length > 0 && (
          <li className="flex flex-wrap justify-between gap-x-2 pt-1 text-muted-foreground">
            <span>Rekentools</span>
            <span>{b.tools.map((t) => `${toolLabel(t.pad)} ${GETAL.format(t.weergaven)}`).join(' · ')}</span>
          </li>
        )}
      </ul>
    </Vak>
  );
}

/* ── inhoud ─────────────────────────────────────────────────────────── */

function InhoudVak({ inhoud }: { inhoud: InhoudStand[] }) {
  const stand = new Map(inhoud.map((i) => [i.soort, i]));
  return (
    <Vak titel="Inhoud" rechts={<Link to={SITE_PAD} className="text-xs text-muted-foreground hover:text-foreground">Alles</Link>}>
      <ul className="px-4 py-1 text-xs">
        {SOORTEN.map((s) => {
          const i = stand.get(s.sleutel);
          return (
            <li key={s.sleutel} className="flex items-center justify-between gap-2 border-b py-1.5 last:border-b-0">
              <Link to={`${SITE_PAD}/${s.sleutel}`} className="truncate hover:underline">{s.meervoud}</Link>
              <span className={cn('shrink-0', i && i.concepten > 0 ? 'text-amber-700' : 'text-muted-foreground')}>
                {i ? (
                  <>
                    {i.aantal}
                    {i.concepten > 0 && ` · ${i.concepten} concept${i.concepten === 1 ? '' : 'en'}`}
                    {i.aantal === 0 && ' · leeg'}
                    {i.laatst && i.aantal > 0 && ` · ${geledenKort(i.laatst)}`}
                  </>
                ) : '—'}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap gap-1.5 border-t px-4 py-2.5">
        {[[PAGINAS_PAD, "Pagina's"], [INSTELLINGEN_PAD, 'Instellingen'], [MEDIA_PAD, 'Media'], [DOORVERWIJZINGEN_PAD, 'Doorverwijzingen']].map(([pad, label]) => (
          <Button key={pad} asChild variant="outline" size="sm" className="h-7 text-xs"><Link to={`${SITE_PAD}/${pad}`}>{label}</Link></Button>
        ))}
      </div>
    </Vak>
  );
}

/* ── laatst gebeurd ─────────────────────────────────────────────────── */

const SOORT_NAAM = new Map(SOORTEN.map((s) => [s.sleutel, s.enkelvoud.toLowerCase()]));

function gebeurtenisTekst(g: Gebeurtenis): string {
  if (g.wat === 'ververs') {
    if (g.uitkomst === 'gelukt') return 'site ververst';
    if (g.uitkomst === 'niet_ingesteld') return 'verversen nog niet ingesteld';
    return `verversen mislukt${g.reden ? ` (${g.reden})` : ''}`;
  }
  const soort = g.soort === 'paginas' ? 'pagina' : SOORT_NAAM.get(g.soort ?? '') ?? g.soort ?? 'inhoud';
  return `${soort} "${g.titel ?? g.slug ?? ''}" gewijzigd`;
}

function Laatst({ laatst }: { laatst: Gebeurtenis[] }) {
  if (laatst.length === 0) return null;
  return (
    <section className="rounded-xl border bg-background px-4 py-2.5 text-xs" aria-label="Laatst gebeurd">
      <span className="font-semibold">Laatst gebeurd</span>
      <span className="text-muted-foreground">
        {laatst.slice(0, 8).map((g, i) => (
          <span key={`${g.op}-${i}`}>
            {' · '}
            <span className={cn(g.wat === 'ververs' && g.uitkomst === 'mislukt' && 'text-red-700')}>{gebeurtenisTekst(g)}</span> ({geledenKort(g.op)})
          </span>
        ))}
      </span>
    </section>
  );
}

/* ── het scherm ─────────────────────────────────────────────────────── */

function Signaalregel({ s }: { s: Signalen }) {
  const n = aantalSignalen(s);
  return <Tegel label="Signalen" waarde={String(n)} onder={n > 0 ? signaalTeksten(s).join(' · ') : 'niets dat aandacht vraagt'} dringend={n > 0} />;
}

function Cijfers({ d, venster }: { d: OverzichtData; venster: Venster }) {
  const b = d.bezoek;
  const tools = b.tools.reduce((n, t) => n + t.weergaven, 0);
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Tegel label={`Bezoekers, ${venster} dagen`} waarde={GETAL.format(b.sessies)} onder={verschilTekst(b.sessies, b.sessies_vorig, venster)} />
      <Tegel label="Formulieren, 7 dagen" waarde={GETAL.format(d.formulieren.laatste_7_dagen)}
        onder={d.formulieren.nieuw > 0 ? `${d.formulieren.nieuw} nog niet opgepakt` : 'alles opgepakt'} naar="/crm/leads" />
      <Tegel label={`Rekentools bekeken, ${venster} dagen`} waarde={GETAL.format(tools)}
        onder={b.tools.length ? b.tools.map((t) => `${toolLabel(t.pad)} ${t.weergaven}`).join(' · ') : null} />
      <Signaalregel s={d.signalen} />
    </div>
  );
}

function Leeg({ fout, opnieuw }: { fout: Error; opnieuw: () => void }) {
  const soort = fout instanceof OverzichtFout ? fout.soort : 'anders';
  return (
    <div className="rounded-xl border bg-background px-4 py-6 text-sm">
      {soort === 'niet_ingericht' ? (
        <>
          <p className="font-medium">De samenvatting is nog niet ingericht.</p>
          <p className="mt-1 text-muted-foreground">Migratie 110 (website.overzicht) is nog niet toegepast op de database. Tot die tijd werken de losse schermen gewoon: <Link to={SITE_PAD} className="underline">content op de site</Link>.</p>
        </>
      ) : soort === 'geen_toegang' ? (
        <p className="text-muted-foreground">Dit scherm is voor het CRM-team.</p>
      ) : (
        <>
          <p className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4 text-destructive" /> De samenvatting kon niet worden opgehaald.</p>
          <p className="mt-1 text-muted-foreground">{fout.message}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={opnieuw}><RefreshCw className="mr-1 h-3.5 w-3.5" /> Opnieuw</Button>
        </>
      )}
    </div>
  );
}

export function WebsiteOverzichtScherm() {
  const [venster, zetVenster] = useState<Venster>(7);
  const q = useOverzicht(venster);
  const d = q.data;
  const ververs = d ? verversTekst(d.signalen) : null;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold"><Globe className="h-6 w-6" /> Website</h1>
          <p className="text-sm text-muted-foreground">
            greenvision.energy{ververs ? ` · ${ververs}` : ''}
            {' · '}<Link to="/crm/website/planning" className="hover:text-foreground hover:underline">planning en taken</Link>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm"><a href={SITE} target="_blank" rel="noopener noreferrer">Bekijk site <ExternalLink className="ml-1 h-3.5 w-3.5" /></a></Button>
          <Button asChild size="sm"><Link to={`${SITE_PAD}/nieuws/nieuw`}><Plus className="mr-1 h-3.5 w-3.5" /> Nieuw bericht</Link></Button>
        </div>
      </div>

      {q.isLoading && <div className="h-40 animate-pulse rounded-xl border bg-muted/30" aria-busy />}
      {q.error && <Leeg fout={q.error} opnieuw={() => q.refetch()} />}
      {d && (
        <>
          <Cijfers d={d} venster={venster} />
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <FormulierenVak f={d.formulieren} />
            <BezoekVak b={d.bezoek} venster={venster} zetVenster={zetVenster} />
            <InhoudVak inhoud={d.inhoud} />
          </div>
          <Laatst laatst={d.laatst} />
          <p className="text-right text-xs text-muted-foreground">
            <Link to={SITE_PAD} className="inline-flex items-center gap-1 hover:text-foreground">Alle content op de site <ArrowRight className="h-3 w-3" /></Link>
          </p>
        </>
      )}
    </div>
  );
}

export default WebsiteOverzichtScherm;
